import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { inputCls, primaryBtnCls, Field, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

export default function LabDataExport() {
  const { can } = useAuth();
  const [entity, setEntity] = useState("bills");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadHistory = () => api.get("/lab/business/export/history").then(({ data }) => setHistory(data.items)).catch(() => setHistory([]));
  useEffect(() => { loadHistory(); }, []);

  const run = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get("/lab/business/export", { params: { entity, from_date: from, to_date: to } });
      const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data.csv], { type: "text/csv" })); a.download = data.filename; a.click();
      toast.success(`Exported ${data.rows} ${entity}.`); loadHistory();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  if (!can("lab.reports.export")) return <NoAccess what="data export" />;

  return (
    <div data-testid="lab-data-export-page">
      <PageHeader overline="Business" title="Data Export" subtitle="Export bills, transactions, patients or expenses to CSV for a date range." />
      {error && <ErrorState message={error} />}
      <div className="panel p-5 grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Field label="Entity"><select className={inputCls} value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="export-entity"><option value="bills">Bills</option><option value="transactions">Transactions</option><option value="patients">Patients</option><option value="expenses">Expenses</option></select></Field>
        <Field label="From"><input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-from" /></Field>
        <Field label="To"><input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-to" /></Field>
        <div className="flex items-end"><button disabled={busy} onClick={run} className={primaryBtnCls} data-testid="run-export-btn">{busy ? "Exporting…" : "Run export"}</button></div>
      </div>
      <p className="overline text-slate-400 mb-3">Export history</p>
      {!history ? <Loading /> : (
        <Table testid="lab-export-history-table" columns={["Entity", "From", "To", "Rows", "By", "At"]}
          rows={history} empty={<EmptyState title="No exports yet" subtitle="Run your first export above." testid="lab-export-empty" />}
          renderRow={(h) => (
            <tr key={h.export_id} data-testid={`export-row-${h.export_id}`}>
              <Td className="capitalize font-medium">{h.entity}</Td><Td className="text-slate-600">{h.from}</Td><Td className="text-slate-600">{h.to}</Td>
              <Td className="text-slate-700">{h.rows}</Td><Td className="text-slate-500 text-xs">{h.by}</Td><Td className="text-slate-500 text-xs">{(h.at || "").slice(0, 16).replace("T", " ")}</Td>
            </tr>
          )} />
      )}
    </div>
  );
}
